import { useState, useCallback, useRef, useEffect } from "react";

export function useKeyboardNav(records, columns, onStartEdit) {
    const [focusedCell, setFocusedCell] = useState(null);

    // Stable ref for onStartEdit so handleKeyDown doesn't recreate
    const onStartEditRef = useRef(onStartEdit);
    useEffect(() => { onStartEditRef.current = onStartEdit; }, [onStartEdit]);

    // Use .length so we don't depend on the full array reference
    const rowCount = records.length;
    const colCount = columns.length;

    // Keep a ref to columns for Enter handler (needs column objects, not just count)
    const columnsRef = useRef(columns);
    useEffect(() => { columnsRef.current = columns; }, [columns]);
    const recordsRef = useRef(records);
    useEffect(() => { recordsRef.current = records; }, [records]);

    const moveFocus = useCallback((direction) => {
        setFocusedCell((prev) => {
            if (!prev) return { row: 0, col: 0 };

            const { row, col } = prev;
            const maxRow = rowCount - 1;
            const maxCol = colCount - 1;

            switch (direction) {
                case "up":    return { row: Math.max(0, row - 1), col };
                case "down":  return { row: Math.min(maxRow, row + 1), col };
                case "left":  return { row, col: Math.max(0, col - 1) };
                case "right": return { row, col: Math.min(maxCol, col + 1) };
                default:      return prev;
            }
        });
    }, [rowCount, colCount]);

    const handleKeyDown = useCallback((e, isEditing) => {
        if (isEditing) return;

        switch (e.key) {
            case "ArrowUp":
                e.preventDefault();
                moveFocus("up");
                break;
            case "ArrowDown":
                e.preventDefault();
                moveFocus("down");
                break;
            case "ArrowLeft":
                e.preventDefault();
                moveFocus("left");
                break;
            case "ArrowRight":
                e.preventDefault();
                moveFocus("right");
                break;
            case "Tab":
                e.preventDefault();
                moveFocus(e.shiftKey ? "left" : "right");
                break;
            case "Enter":
                setFocusedCell((current) => {
                    if (current && onStartEditRef.current) {
                        e.preventDefault();
                        const record = recordsRef.current[current.row];
                        const column = columnsRef.current[current.col];
                        if (record && column) onStartEditRef.current(record.id, column.name);
                    }
                    return current;
                });
                break;
        }
    }, [moveFocus]);

    const isFocused = useCallback((rowIndex, colIndex) => {
        return focusedCell?.row === rowIndex && focusedCell?.col === colIndex;
    }, [focusedCell]);

    return { focusedCell, setFocusedCell, handleKeyDown, isFocused };
}
